import React, { useCallback, useEffect, useState } from 'react';
import { Input, Space, Button, Tag, Dropdown, Modal, message, InputNumber, Popover, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CommonTable from 'components/CommonTable';
import { ITeamMember } from 'types/operation';
import { ERoleDisplayName } from 'types/enumItems';
import {
    getTeamMemberList,
    getTeamCountsSummary,
    updateTeamMemberStatus,
    resendTeamMemberInvite,
    resumeTeamMemberListing,
    pauseTeamMemberListing,
} from 'api/operation/group';
import OpsAddEditStaff from './OpsAddEditStaff';
import s from './s.module.less';

const ROLE_CODE_DISPLAY: Record<string, string> = {
    GROUP_OWNER: 'Group owner',
    GROUP_ADMINISTRATIVE_ASSISTANT: ERoleDisplayName.GROUP_ADMINISTRATIVE_ASSISTANT,
    GROUP_CLINICAL_ASSISTANT: ERoleDisplayName.GROUP_CLINICAL_ASSISTANT,
    GROUP_PRACTICE_MANAGER: ERoleDisplayName.GROUP_PRACTICE_MANAGER,
    GROUP_PROVIDER: ERoleDisplayName.GROUP_PROVIDER,
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
    ACTIVE: { color: '#15803d', bg: '#dcfce7', label: 'Active' },
    PENDING: { color: '#c2410c', bg: '#fff7ed', label: 'Pending' },
    DISABLED: { color: '#6b7280', bg: '#f3f4f6', label: 'Inactive' },
    PAUSED: { color: '#92400e', bg: '#fef3c7', label: 'Paused' },
};

interface GroupPracticeTabProps {
    organizationId?: number;
}

const PAGE_SIZE = 10;

type PauseOption = '1day' | 'custom' | 'indefinitely';

const PauseListingPopover: React.FC<{
    record: ITeamMember;
    onSave: (record: ITeamMember, option: PauseOption, days?: number) => void;
    children: React.ReactNode;
}> = ({ record, onSave, children }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<PauseOption>('1day');
    const [customDays, setCustomDays] = useState<number | null>(null);

    const handleSave = () => {
        onSave(record, selected, selected === 'custom' ? customDays || 1 : undefined);
        setOpen(false);
        setSelected('1day');
        setCustomDays(null);
    };

    const content = (
        <div className={s.pausePopover}>
            <div
                className={`${s.pauseOption} ${selected === '1day' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('1day')}
            >
                <span>Pause for 1 day</span>
                {selected === '1day' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div
                className={`${s.pauseOption} ${selected === 'custom' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('custom')}
            >
                <span>Pause for</span>
                <InputNumber
                    min={1}
                    max={365}
                    value={customDays}
                    onChange={(v) => {
                        setCustomDays(v);
                        setSelected('custom');
                    }}
                    className={s.pauseDaysInput}
                    size="small"
                />
                <span>days</span>
                {selected === 'custom' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div
                className={`${s.pauseOption} ${selected === 'indefinitely' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('indefinitely')}
            >
                <span>Pause indefinitely</span>
                {selected === 'indefinitely' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div className={s.pauseActions}>
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="primary" onClick={handleSave}>
                    Save
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
        >
            {children}
        </Popover>
    );
};

const GroupPracticeTab: React.FC<GroupPracticeTabProps> = ({ organizationId }) => {
    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [data, setData] = useState<ITeamMember[]>([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);
    const [editingMember, setEditingMember] = useState<ITeamMember | undefined>(undefined);
    const [remindingId, setRemindingId] = useState<number | null>(null);
    const [listedProviders, setListedProviders] = useState(0);
    const [activeStaff, setActiveStaff] = useState(0);

    const fetchData = useCallback(
        async (page: number) => {
            if (!organizationId) return;
            try {
                setLoading(true);
                const result = await getTeamMemberList(organizationId, {
                    current: page,
                    size: PAGE_SIZE,
                });
                if (!result.error) {
                    const resp = result.data?.data;
                    setData(resp?.records || []);
                    setTotal(resp?.total || 0);
                    setCurrent(resp?.current || page);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        [organizationId],
    );

    const fetchCountsSummary = useCallback(async () => {
        if (!organizationId) return;
        const res = await getTeamCountsSummary(organizationId);
        if (!res.error) {
            setListedProviders(res.data?.data?.listedProviderCount || 0);
            setActiveStaff(res.data?.data?.activeStaffCount || 0);
        }
    }, [organizationId]);

    useEffect(() => {
        if (organizationId) {
            fetchData(1);
            fetchCountsSummary();
        }
    }, [organizationId]);

    const handleAddStaff = () => {
        setEditingMember(undefined);
        setShowAddStaffModal(true);
    };

    const handleEditMember = (record: ITeamMember) => {
        if (record.roleCode === 'GROUP_PROVIDER' || record.roleCode === 'GROUP_OWNER') {
            window.open(`/operation/uniprofile-detail/-1/${record.providerId}`, '_blank');
            return;
        }
        setEditingMember(record);
        setShowAddStaffModal(true);
    };

    const handleResendInvite = async (record: ITeamMember) => {
        if (!organizationId) return;
        setRemindingId(record.id);
        try {
            const res = await resendTeamMemberInvite(organizationId, record.id);
            if (res?.error) {
                message.error(res.error);
            } else {
                message.success('Invite resent');
            }
        } catch {
            message.error('Failed to resend invite');
        } finally {
            setRemindingId(null);
        }
    };

    const handleDeactivate = (record: ITeamMember) => {
        if (!organizationId) return;
        Modal.confirm({
            title: 'Deactivate team member',
            content: `Are you sure you want to deactivate ${record.firstName} ${record.lastName}? They will lose access to the practice portal.`,
            okText: 'Deactivate',
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await updateTeamMemberStatus(organizationId, record.id, { status: 'DISABLED' });
                if (res.error) return;
                message.success('Member deactivated');
                fetchData(current);
            },
        });
    };

    const handleReactivate = (record: ITeamMember) => {
        if (!organizationId) return;
        Modal.confirm({
            title: 'Reactivate team member',
            content: `Are you sure you want to reactivate ${record.firstName} ${record.lastName}?`,
            okText: 'Reactivate',
            onOk: async () => {
                const res = await updateTeamMemberStatus(organizationId, record.id, { status: 'ACTIVE' });
                if (res.error) return;
                message.success('Member reactivated');
                fetchData(current);
            },
        });
    };

    const handlePauseListing = async (record: ITeamMember, option: PauseOption, days?: number) => {
        if (!organizationId) return;
        let pauseDays: number | undefined;
        if (option === '1day') pauseDays = 1;
        else if (option === 'custom') pauseDays = days || 1;

        const res = await pauseTeamMemberListing(organizationId, record.id, pauseDays ? { pauseDays } : undefined);
        if (res.error) return;

        const reactivateAt = res?.data?.data?.reactivateAt;
        if (reactivateAt) {
            const dateStr = new Date(reactivateAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
            message.success(`Provider paused. Listing will reactivate on ${dateStr}.`);
        } else {
            message.success('Provider paused successfully.');
        }
        fetchData(current);
    };

    const handleResumeListing = async (record: ITeamMember) => {
        if (!organizationId) return;
        const res = await resumeTeamMemberListing(organizationId, record.id);
        if (res.error) return;
        message.success('Provider resumed. Listing is now visible to patients.');
        fetchData(current);
    };

    const addMemberMenuItems: MenuProps['items'] = [
        {
            key: 'add-staff',
            label: 'Add a staff',
            onClick: handleAddStaff,
        },
    ];

    const filteredData = nameFilter
        ? data.filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(nameFilter.toLowerCase()))
        : data;

    const sortedData = [...filteredData].sort((a, b) => {
        if (a.isOwner && !b.isOwner) return -1;
        if (!a.isOwner && b.isOwner) return 1;
        return 0;
    });

    const providerMembers = data.filter((m) => m.roleCode === 'GROUP_PROVIDER');

    const columns: ColumnsType<ITeamMember> = [
        {
            title: 'NAME',
            dataIndex: 'firstName',
            key: 'name',
            width: 200,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onPressEnter={() => {
                            setNameFilter(pendingNameFilter);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingNameFilter);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: <SearchOutlined />,
            render: (_: string, record: ITeamMember) => (
                <div>
                    <div className={s.nameCell}>
                        {record.firstName} {record.lastName}
                    </div>
                    {record.email && <div className={s.email}>{record.email}</div>}
                </div>
            ),
        },
        {
            title: 'ROLE',
            dataIndex: 'roleCode',
            key: 'role',
            width: 280,
            render: (roleCode: string, record: ITeamMember) => (
                <div className={s.roleCell}>
                    <span>{ROLE_CODE_DISPLAY[roleCode] || roleCode}</span>
                    {record.isAdmin && <Tag color="blue">Admin</Tag>}
                    {record.isOwner && <Tag color="purple">Owner</Tag>}
                </div>
            ),
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (status: string, record: ITeamMember) => {
                if (record.listingStatus === 'PL') {
                    const pausedCfg = STATUS_CONFIG.PAUSED;
                    if (record.listingReactivateAt) {
                        const dateStr = dayjs(record.listingReactivateAt).format('MMM D, YYYY');
                        return (
                            <span
                                className={s.statusBadge}
                                style={{ color: pausedCfg.color, backgroundColor: pausedCfg.bg }}
                            >
                                Paused - {dateStr}
                            </span>
                        );
                    }
                    return (
                        <span
                            className={s.statusBadge}
                            style={{ color: pausedCfg.color, backgroundColor: pausedCfg.bg }}
                        >
                            Paused
                        </span>
                    );
                }
                const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DISABLED;
                return (
                    <span className={s.statusBadge} style={{ color: cfg.color, backgroundColor: cfg.bg }}>
                        {cfg.label}
                    </span>
                );
            },
        },
        {
            title: 'ASSIGN PROVIDER',
            dataIndex: 'isAllProviders',
            key: 'assignedProviders',
            width: 220,
            render: (_: any, record: ITeamMember) => {
                if (record.isAllProviders) return <span>All providers</span>;
                const providers = record.assignedProviders;
                if (providers && providers.length > 0) {
                    const tooltipContent = (
                        <div style={{ whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {providers.map((p) => {
                                const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
                                return (
                                    <div key={p.providerId}>
                                        {name ? `${name} - ID: ${p.providerId}` : `ID: ${p.providerId}`}
                                    </div>
                                );
                            })}
                        </div>
                    );
                    return (
                        <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: 500 }}>
                            <span style={{ cursor: 'pointer' }}>
                                {providers.length} provider{providers.length > 1 ? 's' : ''}
                            </span>
                        </Tooltip>
                    );
                }
                return <span>—</span>;
            },
        },
        {
            title: 'LAST ACTIVITY',
            dataIndex: 'lastActiveAt',
            key: 'lastActiveAt',
            width: 140,
            render: (val: string | null) => <span>{val ? dayjs(val).format('MM/DD/YYYY') : '—'}</span>,
        },
        {
            title: null,
            key: 'action',
            render: (_: any, record: ITeamMember) => {
                const isPending = record.status === 'PENDING';
                const isActive = record.status === 'ACTIVE';
                const isDisabled = record.status === 'DISABLED';
                const isOwner = record.roleCode === 'GROUP_OWNER';
                const isStaff = [
                    'GROUP_ADMINISTRATIVE_ASSISTANT',
                    'GROUP_CLINICAL_ASSISTANT',
                    'GROUP_PRACTICE_MANAGER',
                ].includes(record.roleCode);

                if (isOwner) {
                    return (
                        <span className={s.actionCol2}>
                            <Button type="link" onClick={() => handleEditMember(record)}>
                                Edit
                            </Button>
                        </span>
                    );
                }

                return (
                    <div className={s.actionCell}>
                        <span className={s.actionCol1}>
                            {!isStaff && (
                                <span className={s.actionCol2}>
                                    <Button type="link" onClick={() => handleEditMember(record)}>
                                        Edit
                                    </Button>
                                </span>
                            )}
                            {/* {isPending && (
                                <Button
                                    type="link"
                                    loading={remindingId === record.id}
                                    onClick={() => handleResendInvite(record)}
                                >
                                    Resend invite
                                </Button>
                            )} */}
                            {!isPending && (
                                <span className={s.actionCol3}>
                                    {(() => {
                                        if (isActive && record.roleCode !== 'GROUP_PROVIDER') {
                                            return (
                                                <Button type="link" onClick={() => handleDeactivate(record)}>
                                                    Deactivate
                                                </Button>
                                            );
                                        }
                                        if (isDisabled) {
                                            return (
                                                <Button type="link" onClick={() => handleReactivate(record)}>
                                                    Reactivate
                                                </Button>
                                            );
                                        }
                                        return '-';
                                    })()}
                                </span>
                            )}
                        </span>

                        {record.roleCode === 'GROUP_PROVIDER' && (
                            <span className={s.actionCol2}>
                                {record.listingStatus === 'PL' && (
                                    <Button type="link" onClick={() => handleResumeListing(record)}>
                                        Resume listing
                                    </Button>
                                )}
                                {record.listingStatus === 'L' && (
                                    <PauseListingPopover record={record} onSave={handlePauseListing}>
                                        <Button type="link">Pause listing</Button>
                                    </PauseListingPopover>
                                )}
                            </span>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h2 className={s.title}>Team</h2>
                {/* <Dropdown menu={{ items: addMemberMenuItems }} trigger={['hover']}>
                    <Button>
                        Add new team member <DownOutlined />
                    </Button>
                </Dropdown> */}
            </div>
            <div className={s.metrics}>
                <div className={s.metricCard}>
                    <span className={s.metricLabel}>Listed providers</span>
                    <span className={s.metricValue}>{listedProviders}</span>
                </div>
                <div className={s.metricCard}>
                    <span className={s.metricLabel}>Active staff</span>
                    <span className={s.metricValue}>{activeStaff}</span>
                </div>
            </div>
            <CommonTable
                bordered
                rowKey="id"
                columns={columns}
                data={sortedData}
                loading={loading}
                scroll={{ x: 1200 }}
                pagination={{
                    current,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: (page) => fetchData(page),
                    showSizeChanger: false,
                }}
            />
            {organizationId && (
                <OpsAddEditStaff
                    show={showAddStaffModal}
                    organizationId={organizationId}
                    item={editingMember}
                    providerMembers={providerMembers}
                    onCancel={() => {
                        setShowAddStaffModal(false);
                        setEditingMember(undefined);
                    }}
                    onSuccess={() => {
                        setShowAddStaffModal(false);
                        setEditingMember(undefined);
                        fetchData(current);
                    }}
                />
            )}
        </div>
    );
};

export default GroupPracticeTab;
